import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Flame, Sparkles, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { isRTL, useLanguageStore } from "@/stores/languageStore";
import { fetchProducts, ShopifyProduct } from "@/lib/shopify";
import { HotDealProductCard } from "@/components/HotDealProductCard";

// Sale end date: January 20, 2026 at midnight (Israel time)
const SALE_END_DATE = new Date("2026-01-20T00:00:00+02:00");

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const calculateTimeLeft = (): TimeLeft | null => {
  const now = new Date();
  const difference = SALE_END_DATE.getTime() - now.getTime();
  
  if (difference <= 0) {
    return null; // Sale ended
  }
  
  return {
    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
    hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((difference / 1000 / 60) % 60),
    seconds: Math.floor((difference / 1000) % 60),
  };
};

export const HotDealsSection = () => {
  const { t, language } = useLanguageStore();
  const rtl = isRTL(language);
  const ArrowIcon = rtl ? ArrowLeft : ArrowRight;
  const arrowSpacing = rtl ? "mr-2" : "ml-2";
  
  const [hotDeals, setHotDeals] = useState<ShopifyProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(calculateTimeLeft);

  // Countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft();
      setTimeLeft(newTimeLeft);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const loadHotDeals = async () => {
      try {
        // Fetch products with "hot deal" tag
        const response = await fetchProducts(10, "tag:\"hot deal\"");
        setHotDeals(response.products);
      } catch (error) {
        console.error("Error loading hot deals:", error);
      } finally {
        setLoading(false);
      }
    };

    loadHotDeals();
  }, []);

  // Don't render section if sale ended or no hot deals
  if (timeLeft === null) {
    return null;
  }
  
  if (!loading && hotDeals.length === 0) {
    return null;
  }

  return (
    <section className="py-16 bg-gradient-to-b from-charcoal via-charcoal/98 to-charcoal relative overflow-hidden">
      {/* Subtle background accent */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/20 rounded-full blur-[120px]" />
      </div>
      
      <div className="container relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-3 border border-primary/30 bg-primary/10 text-primary px-5 py-2.5 rounded-full mb-5">
            <Sparkles className="h-4 w-4" />
            <span className="font-medium text-sm tracking-wide">{t('hotDeals.badge')}</span>
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">
            {t('hotDeals.title')}
          </h2>
          <p className="text-white/60 max-w-md mx-auto mb-8">
            {t('hotDeals.description')}
          </p>
          
          {/* Countdown Timer - Premium Style */}
          <div className="flex justify-center gap-3 md:gap-4 mb-4">
            <div className="bg-charcoal-light border border-white/10 rounded-lg p-3 md:p-4 min-w-[70px] md:min-w-[80px]">
              <div className="text-2xl md:text-3xl font-bold text-white">{String(timeLeft.days).padStart(2, '0')}</div>
              <div className="text-xs text-white/50 mt-1">{t('hotDeals.days')}</div>
            </div>
            <div className="bg-charcoal-light border border-white/10 rounded-lg p-3 md:p-4 min-w-[70px] md:min-w-[80px]">
              <div className="text-2xl md:text-3xl font-bold text-white">{String(timeLeft.hours).padStart(2, '0')}</div>
              <div className="text-xs text-white/50 mt-1">{t('hotDeals.hours')}</div>
            </div>
            <div className="bg-charcoal-light border border-white/10 rounded-lg p-3 md:p-4 min-w-[70px] md:min-w-[80px]">
              <div className="text-2xl md:text-3xl font-bold text-white">{String(timeLeft.minutes).padStart(2, '0')}</div>
              <div className="text-xs text-white/50 mt-1">{t('hotDeals.minutes')}</div>
            </div>
            <div className="bg-charcoal-light border border-white/10 rounded-lg p-3 md:p-4 min-w-[70px] md:min-w-[80px]">
              <div className="text-2xl md:text-3xl font-bold text-white">{String(timeLeft.seconds).padStart(2, '0')}</div>
              <div className="text-xs text-white/50 mt-1">{t('hotDeals.seconds')}</div>
            </div>
          </div>
          <p className="text-primary/80 text-sm flex items-center justify-center gap-2">
            <Clock className="h-4 w-4" />
            {t('hotDeals.endsAt')}
          </p>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {hotDeals.map((product) => (
              <div key={product.node.id} className="relative group">
                {/* Sale badge - elegant style */}
                <div className="absolute -top-2 -right-2 z-20 bg-primary text-primary-foreground px-3 py-1 rounded-md text-sm font-medium shadow-md">
                  {t('hotDeals.saleBadge')}
                </div>
                <HotDealProductCard product={product} />
              </div>
            ))}
          </div>
        )}

        {/* View All Link */}
        <div className="text-center mt-10">
          <Link to="/products?query=tag:hot deal">
            <Button size="lg" className="gradient-gold text-white shadow-elegant">
              {t('hotDeals.viewAll')}
              <ArrowIcon className={`${arrowSpacing} h-5 w-5`} />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};
